import React, { useState } from 'react';
import { ArrowLeft, Filter } from 'lucide-react';
import TaskCard from '@/components/common/TaskCard';
import { Task } from '@/types';

interface AllTasksScreenProps {
  tasks: Task[];
  onBack: () => void;
  onTaskComplete: (taskId: string) => void;
  onTaskClick: (taskId: string) => void;
  showHeader?: boolean;
}

const AllTasksScreen: React.FC<AllTasksScreenProps> = ({
  tasks,
  onBack,
  onTaskComplete,
  onTaskClick,
  showHeader = true,
}) => {
  const [filterOption, setFilterOption] = useState<'All' | 'Tasks'>('All');
  const [showFilterPopup, setShowFilterPopup] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-background">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={24} className="text-gray-800" />
          </button>
          <h1 className="text-2xl font-semibold text-black">All Tasks</h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {/* Filter Bar */}
        <div className="mb-4 flex items-center justify-between">
          <div className="text-gray-600 text-sm">
            {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
          </div>
          <button
            onClick={() => setShowFilterPopup(!showFilterPopup)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
          >
            <Filter size={20} className="text-gray-600" />
            {showFilterPopup && (
              <div className="absolute right-0 top-full mt-1 bg-background border border-gray-200 rounded-xl shadow-xl z-50 min-w-[120px]">
                {(['All', 'Tasks'] as const).map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setFilterOption(option);
                      setShowFilterPopup(false);
                    }}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                      filterOption === option
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-gray-700'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </button>
        </div>

        {/* Tasks List */}
        {tasks.length > 0 ? (
          <div className="space-y-3 pb-20">
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onTaskComplete={onTaskComplete}
                onTaskClick={onTaskClick}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <p>No tasks available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllTasksScreen;
